import { useEffect, useState } from "react";
import type { MaterialMapInfo, MaterialMapSlot, TextureTransform } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { useDomainState } from "../../../../state/editor/useEditor";
import { pickFile } from "../../../../util/download";
import { CompactCheck, CompactNumber, MiniBtn, Row } from "../../inspector/rows";

const SLOT_LABEL: Record<MaterialMapSlot, string> = {
  map: "Base Color", roughnessMap: "Roughness", metalnessMap: "Metalness", normalMap: "Normal",
  bumpMap: "Bump", displacementMap: "Displacement", aoMap: "AO", lightMap: "Light",
  emissiveMap: "Emissive", alphaMap: "Alpha", clearcoatMap: "Clearcoat", clearcoatRoughnessMap: "Clearcoat Rough",
  clearcoatNormalMap: "Clearcoat Normal", sheenColorMap: "Sheen Color", sheenRoughnessMap: "Sheen Rough",
  iridescenceMap: "Iridescence", iridescenceThicknessMap: "Iridescence Thick", transmissionMap: "Transmission",
  thicknessMap: "Thickness",
  // v31: secondary bump channel layered over the primary bumpMap (webgi
  // CustomBumpMapPlugin).
  customBumpMap: "Custom Bump",
};

/** Work in flight for ONE slot. Twenty slots share this panel and each load is
 * an independent decode/fetch, so busy is tracked per slot — a single global
 * flag would freeze all twenty while one 4K normal map uploads. */
type SlotBusy = "file" | "url" | "clear";

const errText = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Texture load failed.";

/** Texture-map slots for the selected (non-gem) material (v20). Each slot loads
 * an image by URL/file, clears, and (when present) edits repeat/rotation. */
export function MaterialMapsControl() {
  const { client } = useViewer();
  const r = client.ring;
  const groupId = useDomainState("selection") as string | null;
  const [maps, setMaps] = useState<MaterialMapInfo[]>([]);
  const [urlSlot, setUrlSlot] = useState<MaterialMapSlot | null>(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState<Partial<Record<MaterialMapSlot, SlotBusy>>>({});
  const [errors, setErrors] = useState<Partial<Record<MaterialMapSlot, string>>>({});
  const [listError, setListError] = useState<string | null>(null);

  const setBusyAt = (slot: MaterialMapSlot, v: SlotBusy | undefined) =>
    setBusy((prev) => ({ ...prev, [slot]: v }));
  const setErrorAt = (slot: MaterialMapSlot, v: string | undefined) =>
    setErrors((prev) => ({ ...prev, [slot]: v }));

  const refresh = () => {
    if (!groupId) { setMaps([]); setListError(null); return; }
    // A failed listing used to be reported as "no slots", which renders the
    // gem-material hint — a wrong explanation for an RPC failure.
    r.listGroupMaterialMaps(groupId).then(
      (m) => { setMaps(m); setListError(null); },
      (e: unknown) => { setMaps([]); setListError(errText(e)); },
    );
  };
  useEffect(refresh, [groupId, r]);
  if (!groupId) return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Select a metal/standard part to edit its texture maps.</span>;
  if (listError) return <span className="block px-3 py-1.5 text-[11px] text-ed-red">{listError}</span>;
  // No slots => the group has no PBR material (a gem is pure ShaderMaterial).
  // Say so, the way Material Extensions does, instead of rendering nothing.
  if (!maps.length) return <span className="block px-3 py-1.5 text-[11px] text-ed-text-3">Gem materials use the Diamond section.</span>;

  const loadFile = async (slot: MaterialMapSlot) => {
    if (busy[slot]) return;
    setBusyAt(slot, "file");
    setErrorAt(slot, undefined);
    // Awaiting pickFile holds the spinner across the real File→decode→GPU
    // upload (it only settles once onFile's promise does), and cancelling the
    // dialog just drops the busy flag. The RPC is caught INSIDE onFile because
    // pickFile chains `.finally` onto what we return — a rejection escaping
    // here would surface as an unhandled rejection instead of in the UI.
    await pickFile("image/*", async (f) => {
      try {
        await r.setGroupMaterialMapFile(groupId, slot, f);
        refresh();
      } catch (e) {
        // TextureLoader rejects on an undecodable image; without this the slot
        // silently kept its old (or absent) map.
        setErrorAt(slot, errText(e));
      }
    });
    setBusyAt(slot, undefined);
  };

  const loadUrl = async (slot: MaterialMapSlot) => {
    const u = url.trim();
    // Enter could be held down to fire N concurrent loads into one slot, with
    // whichever resolved last winning.
    if (!u || busy[slot]) return;
    setBusyAt(slot, "url");
    setErrorAt(slot, undefined);
    try {
      await r.setGroupMaterialMapUrl(groupId, slot, u);
      // Only cleared on success, so a 404/CORS failure leaves the URL in the
      // field to fix and retry.
      setUrl("");
      setUrlSlot(null);
      refresh();
    } catch (e) {
      setErrorAt(slot, errText(e));
    } finally {
      setBusyAt(slot, undefined);
    }
  };

  const clear = async (slot: MaterialMapSlot) => {
    if (busy[slot]) return;
    setBusyAt(slot, "clear");
    setErrorAt(slot, undefined);
    try {
      // Awaited before refreshing: the old fire-and-forget listed the maps
      // while the clear was still in flight, so the presence dot could survive.
      await r.clearGroupMaterialMap(groupId, slot);
      refresh();
    } catch (e) {
      setErrorAt(slot, errText(e));
    } finally {
      setBusyAt(slot, undefined);
    }
  };

  // Sampler edits are cheap but still cross the bridge; awaiting keeps the
  // re-listing from racing the write, and a rejection reaches the user.
  const setSampler = async (slot: MaterialMapSlot, transform: Partial<TextureTransform>) => {
    setErrorAt(slot, undefined);
    try {
      await r.setGroupMaterialSampler(groupId, slot, transform);
      refresh();
    } catch (e) {
      setErrorAt(slot, errText(e));
    }
  };

  return (
    <div className="flex flex-col">
      {maps.map((mi) => {
        const b = busy[mi.slot];
        return (
        <div key={mi.slot} className="border-b border-ed-border/60 last:border-0">
          <Row label={SLOT_LABEL[mi.slot]}>
            <div className="flex items-center gap-1">
              {mi.present ? <span className="text-[11px] text-ed-accent">●</span> : null}
              <MiniBtn icon="image" busy={b === "file"} disabled={!!b} onClick={() => void loadFile(mi.slot)}>
                {b === "file" ? "Loading…" : mi.present ? "Replace" : "Load"}
              </MiniBtn>
              <MiniBtn icon="link" disabled={!!b} onClick={() => setUrlSlot(urlSlot === mi.slot ? null : mi.slot)}>URL</MiniBtn>
              {mi.present ? (
                <MiniBtn icon="close" busy={b === "clear"} disabled={!!b} onClick={() => void clear(mi.slot)}>Clear</MiniBtn>
              ) : null}
            </div>
          </Row>
          {urlSlot === mi.slot ? (
            <div className="flex items-center gap-1 px-3 pb-1.5">
              <input
                className="min-w-0 flex-1 rounded border border-ed-border bg-white px-2 py-1 text-[12px] disabled:opacity-60"
                placeholder="image URL…"
                value={url}
                disabled={b === "url"}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void loadUrl(mi.slot); }}
              />
              <MiniBtn primary busy={b === "url"} disabled={!!b} onClick={() => void loadUrl(mi.slot)}>Load</MiniBtn>
            </div>
          ) : null}
          {errors[mi.slot] ? <span className="block px-3 pb-1.5 text-[11px] text-ed-red">{errors[mi.slot]}</span> : null}
          {mi.present ? (
            <>
              <div className="flex items-center gap-2 px-3 pb-1.5">
                <span className="text-[11px] text-ed-text-3">Repeat</span>
                <CompactNumber value={mi.transform.repeat.x} step={0.1} width={52} onChange={(v) => void setSampler(mi.slot, { repeat: { ...mi.transform.repeat, x: v } })} />
                <CompactNumber value={mi.transform.repeat.y} step={0.1} width={52} onChange={(v) => void setSampler(mi.slot, { repeat: { ...mi.transform.repeat, y: v } })} />
                <span className="text-[11px] text-ed-text-3">Rot</span>
                <CompactNumber value={mi.transform.rotation} step={0.01} width={52} onChange={(v) => void setSampler(mi.slot, { rotation: v })} />
              </div>
              <div className="flex items-center gap-2 px-3 pb-1.5">
                <span className="text-[11px] text-ed-text-3">Offset</span>
                <CompactNumber value={mi.transform.offset.x} step={0.01} width={52} onChange={(v) => void setSampler(mi.slot, { offset: { ...mi.transform.offset, x: v } })} />
                <CompactNumber value={mi.transform.offset.y} step={0.01} width={52} onChange={(v) => void setSampler(mi.slot, { offset: { ...mi.transform.offset, y: v } })} />
                <span className="text-[11px] text-ed-text-3">Center</span>
                <CompactNumber value={mi.transform.center.x} step={0.01} width={52} onChange={(v) => void setSampler(mi.slot, { center: { ...mi.transform.center, x: v } })} />
                <CompactNumber value={mi.transform.center.y} step={0.01} width={52} onChange={(v) => void setSampler(mi.slot, { center: { ...mi.transform.center, y: v } })} />
              </div>
              <div className="flex items-center gap-2 px-3 pb-1.5">
                <span className="text-[11px] text-ed-text-3">Flip Y</span>
                <CompactCheck checked={mi.transform.flipY} onChange={(v) => void setSampler(mi.slot, { flipY: v })} />
                <span className="text-[11px] text-ed-text-3">Anisotropy</span>
                <CompactNumber value={mi.transform.anisotropy} min={1} max={16} step={1} width={52} onChange={(v) => void setSampler(mi.slot, { anisotropy: v })} />
              </div>
            </>
          ) : null}
        </div>
        );
      })}
    </div>
  );
}
